import { SummaryConfig } from "../../../hooks";
import { capitalizeFirst } from "../../../helper-functions/capitalizeFirst";

export const initialGradingSummaryConfig: SummaryConfig = {
    general: [
        {
            accessor: "debris.checked",
            renderer: (data) => {
                const { other } = data.debris ?? {};
                return {
                    "title": "Debris in vial",
                    "debris.scleral": "Scleral pieces",
                    "debris.conjunctival": "Conjunctival pieces",
                    "debris.choroidal": "Choroidal material",
                    "debris.eyelash": "Eyelash",
                    "debris.epithelialFragments": "Epithelial fragments",
                    "debris.other.checked": other?.comment ? `Other: ${other.comment}` : null,
                };
            },
        },
        {
            accessor: "rbc.checked",
            renderer: {
                "title": "RBCs in vial",
                "rbc.quantity": "",
            },
        },
        {
            accessor: "pterygium.checked",
            renderer: {
                "title": "Pterygium",
                "pterygium.peripheral": "Peripheral",
                "pterygium.midperipheral": "Midperipheral",
                "pterygium.pericentral": "Pericentral",
                "pterygium.central": "Central",
            },
        },
        {
            accessor: "frill.checked",
            renderer: (data) => {
                const { size1, size2, circumference } = data.frill ?? {};
                if (!size1 && !size2 && !circumference) {
                    return "KLAL present.";
                }
                return `KLAL present, size ${size1 ?? ""} mm to ${size2 ?? ""} mm, ${circumference ?? ""} clock-hours of circumference.`;
            },
        },
        {
            accessor: "jaundice.checked",
            renderer: (data) => {
                const { severity, location } = data.jaundice ?? {};
                if (!severity && !location) {
                    return "Jaundice present.";
                }
                return `Jaundice present ${capitalizeFirst(severity)} ${capitalizeFirst(location)}.`;
            },
        },
        {
            accessor: "surgery.checked",
            renderer: (data) => {
                if (!data.surgery?.comment) {
                    return "Refractive surgery evidence present.";
                }
                return `Refractive surgery evidence present: ${data.surgery.comment}.`;
            },
        },
        {
            accessor: "unevenCsRims",
            renderer: "Uneven cs rims.",
        },
        {
            accessor: "remnantsPresent",
            renderer: "Conjunctival remnants.",
        },
        {
            accessor: "cornealDiameter",
            formatter: (val) => `Corneal diameter: ${val} mm.`,
        },
        {
            accessor: "",
            renderer: (data) => {
                const { corneoscleralRimSize1, corneoscleralRimSize2 } = data;
                if (!corneoscleralRimSize1 && !corneoscleralRimSize2) return "";
                return `Corneoscleral Rim Size: ${corneoscleralRimSize1 ?? ""} mm X ${corneoscleralRimSize2 ?? ""} mm.`;
            },
        },
        {
            accessor: "",
            renderer: (data) => {
                const { newClearZone, clearZoneSize, clearZoneNa, endothelial, endothelialNa } = data;
                let result = "";
                if (clearZoneNa) {
                    result = "Primary clear zone: N/A.";
                } else {
                    result = `Primary clear zone: ${newClearZone ?? ""} ${clearZoneSize ?? ""} mm.`;
                }
                if (endothelialNa) {
                    result += " Endothelial clear zone: N/A.";
                } else {
                    result += ` Endothelial clear zone: ${endothelial ?? ""} mm.`;
                }
                return <b>{result}</b>;
            },
        },
        {
            accessor: "lensStatus",
            formatter: (val) => `Lens Staus: ${val ?? ""}.`,
        },
        {
            accessor: "",
            renderer: (data) => {
                const { touched, scraped, grasped } = data;
                if (!touched && !scraped && !grasped) return "";
                return {
                    title: "Technician induced trauma",
                    touched: "Touch",
                    scraped: "Scrape",
                    grasped: "Grasp",
                };
            },
        },
        {
            accessor: "generalComments",
        },
    ],
    epithelium: [
        {
            accessor: "epitheliumClear",
            renderer: "Clear.",
        },
        {
            accessor: "epitheliumIntact",
            renderer: "Intact.",
        },
        {
            accessor: "exposure.checked",
            renderer: {
                "title": "Exposure",
                "exposure.generalized": "Generalized",
                "exposure.inferior": "Inferior",
                "exposure.band": "Band",
                "exposure.severity": "",
            },
        },
        {
            accessor: "denudation.checked",
            renderer: {
                "title": "Denudation",
                "denudation.generalized": "Generalized",
                "denudation.inferior": "Inferior",
                "denudation.patchy": "Patchy",
            },
        },
        {
            accessor: "sloughing.checked",
            renderer: (data) => {
                return {
                    "title": "Sloughing",
                    "sloughing.generalized": "Generalized",
                    "sloughing.patchy": "Patchy",
                    "sloughing.percent": `${data.sloughing?.percent ?? ""}% Sloughed`,
                };
            },
        },
        {
            accessor: "epitheliumComments",
        },
    ],
    stroma: [
        {
            accessor: "stromaClear",
            renderer: "Clear, Compact.",
        },
        {
            accessor: "arcusSenilis.checked",
            renderer: {
                "title": "Arcus Senilis",
                "arcusSenilis.severity": "",
            },
        },
        {
            accessor: "scarring.checked",
            renderer: {
                "title": "Scarring",
                "scarring.quantity": "",
                "scarring.anterior": "Anterior",
                "scarring.midstromal": "Midstromal",
                "scarring.fullThickness": "Full Thickness",
                "scarring.diffuse": "Diffuse",
                "scarring.central": "Central",
                "scarring.pericentral": "Pericentral",
                "scarring.midperipheral": "Midperipheral",
                "scarring.peripheral": "Peripheral",
            },
        },
        {
            accessor: "stria.checked",
            renderer: {
                "title": "Stria",
                "stria.quantity": "",
                "stria.severity": "",
                "stria.diffuse": "Diffuse",
                "stria.pericentral": "Pericentral",
                "stria.midperipheral": "Midperipheral",
                "stria.peripheral": "Peripheral",
            },
        },
        {
            accessor: "stromalEdema.checked",
            renderer: {
                "title": "Stromal Edema",
                "stromalEdema.severity": "",
                "stromalEdema.diffuse": "Diffuse",
                "stromalEdema.central": "Central",
                "stromalEdema.peripheral": "Peripheral",
                "stromalEdema.underlyingSlough": "Underlying Slough",
            },
        },
        {
            accessor: "stromaComments",
        },
    ],
    descemets: [
        {
            accessor: "descemetsClear",
            renderer: "Clear, Compact.",
        },
        {
            accessor: "folds.checked",
            renderer: {
                "title": "Folds",
                "folds.quantity": "",
                "folds.severity": "",
                "folds.diffuse": "Diffuse",
                "folds.central": "Central",
                "folds.pericentral": "Pericentral",
                "folds.midperipheral": "Midperipheral",
                "folds.peripheral": "Peripheral",
            },
        },
        {
            accessor: "descemetsComments",
        },
    ],
    endothelium: [
        {
            accessor: "endotheliumIntact",
            renderer: "Endothelium Intact.",
        },
        {
            accessor: "guttata.checked",
            renderer: {
                "title": "Guttata",
                "guttata.severity": "",
                "guttata.diffuse": "Diffuse",
                "guttata.pericentral": "Pericentral",
            },
        },
        {
            accessor: "polymegathism",
            defaultValue: false,
            formatter: (val) => (val ? "Polymegathism noted." : "Cells are uniform in size."),
        },
        {
            accessor: "pleomorphism",
            defaultValue: false,
            formatter: (val) => (val ? "Pleomorphism noted." : "Cells are uniform in shape."),
        },
        {
            accessor: "stressLines.checked",
            renderer: {
                "title": "Stress Lines",
                "stressLines.quantity": "",
                "stressLines.severity": "",
                "stressLines.diffuse": "Diffuse",
                "stressLines.pericentral": "Pericentral",
                "stressLines.midperipheral": "Midperipheral",
                "stressLines.peripheral": "Peripheral",
            },
        },
        {
            accessor: "cellLoss.checked",
            renderer: {
                "title": "Cell loss",
                "cellLoss.severity": "",
                "cellLoss.diffuse": "Diffuse",
                "cellLoss.concentrated": "Concentrated",
                "cellLoss.foldInduced": "Fold-induced",
            },
        },
        {
            accessor: "endotheliumComments",
        },
    ],
};
